import { motion } from "framer-motion";
import { Mic, BrainCircuit, Zap, MonitorSmartphone, ShieldCheck, Smartphone } from "lucide-react";
import { useLang } from "@/App";

const ICONS = {
    mic: Mic,
    brain: BrainCircuit,
    zap: Zap,
    hud: MonitorSmartphone,
    shield: ShieldCheck,
    phone: Smartphone,
};

const SPANS = [
    "md:col-span-7",
    "md:col-span-5",
    "md:col-span-4",
    "md:col-span-4",
    "md:col-span-4",
    "md:col-span-12",
];

export default function Bento() {
    const { t } = useLang();
    return (
        <section id="features" className="relative py-20 lg:py-28 overflow-hidden" data-testid="features-section">
            <div
                className="absolute inset-0 bg-cover bg-center opacity-[0.07] pointer-events-none"
                style={{ backgroundImage: "url(https://images.unsplash.com/photo-1581578701651-1f7cff5960aa?q=80&w=1600&auto=format&fit=crop)" }}
            />
            <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[#080a12] to-transparent pointer-events-none" />
            <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#080a12] to-transparent pointer-events-none" />
            <div className="kana kana-watermark absolute right-2 top-6 text-[7rem] lg:text-[10rem] leading-none pointer-events-none" aria-hidden="true">
                システム
            </div>
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="mb-12 max-w-2xl"
                >
                    <div className="font-mono2 text-[11px] tracking-[0.3em] text-[#49f5e8] cy-text">{t.bento.kicker}</div>
                    <h2 className="font-display font-bold uppercase tracking-wide text-2xl sm:text-3xl lg:text-4xl mt-3">
                        {t.bento.title}
                    </h2>
                    <p className="text-sm sm:text-base text-slate-400 mt-4 leading-relaxed">{t.bento.sub}</p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-5" data-testid="bento-grid">
                    {t.bento.items.map((item, i) => {
                        const Icon = ICONS[item.icon];
                        const wide = SPANS[i] === "md:col-span-12";
                        return (
                            <motion.div
                                key={item.tag}
                                data-testid={`bento-card-${item.icon}`}
                                initial={{ opacity: 0, y: 34 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.7, delay: (i % 3) * 0.1, ease: [0.16, 1, 0.3, 1] }}
                                className={`glow-card rounded-2xl p-6 sm:p-7 ${SPANS[i]} ${wide ? "md:flex md:items-center md:gap-8" : ""}`}
                                style={
                                    wide
                                        ? {
                                              backgroundImage:
                                                  "linear-gradient(160deg, rgba(11,14,24,0.86), rgba(11,14,24,0.95)), url(/char/voice-art.png)",
                                              backgroundSize: "cover",
                                              backgroundPosition: "center",
                                          }
                                        : undefined
                                }
                            >
                                <div className="flex items-center justify-between">
                                    <div className="w-11 h-11 rounded-xl border border-[#d7ff35]/35 bg-[#d7ff35]/8 grid place-items-center text-[#d7ff35]">
                                        <Icon size={20} />
                                    </div>
                                    <span className="font-mono2 text-[9px] tracking-[0.25em] text-slate-500">{item.tag}</span>
                                </div>
                                <div className={wide ? "md:mt-0" : ""}>
                                    <h3 className="font-display font-bold text-base sm:text-lg tracking-wide mt-5 uppercase">{item.title}</h3>
                                    <p className="text-sm text-slate-400 mt-2.5 leading-relaxed max-w-xl">{item.desc}</p>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
